import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ClipboardList, Trash2 } from "lucide-react";
import { toast } from "sonner";

const QUESTIONS = [
  { key: "itchy", label: "Itchy skin" },
  { key: "sleep_disturbed", label: "Sleep disturbed" },
  { key: "bleeding", label: "Bleeding skin" },
  { key: "weeping", label: "Weeping or oozing skin" },
  { key: "cracked", label: "Cracked skin" },
  { key: "flaking", label: "Flaking skin" },
  { key: "dry_rough", label: "Dry or rough skin" },
];

const OPTIONS = [
  { value: 0, label: "No days" },
  { value: 1, label: "1–2 days" },
  { value: 2, label: "3–4 days" },
  { value: 3, label: "5–6 days" },
  { value: 4, label: "Every day" },
];

function bandColor(total) {
  if (total <= 2) return "bg-emerald-200 text-emerald-900";
  if (total <= 7) return "bg-lime-200 text-lime-900";
  if (total <= 16) return "bg-amber-200 text-amber-900";
  if (total <= 24) return "bg-orange-200 text-orange-900";
  return "bg-rose-200 text-rose-900";
}

function bandLabel(total) {
  if (total <= 2) return "Clear or almost clear";
  if (total <= 7) return "Mild";
  if (total <= 16) return "Moderate";
  if (total <= 24) return "Severe";
  return "Very severe";
}

export default function Poem() {
  const today = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState("history");
  const [scores, setScores] = useState([]);
  const [scoreDate, setScoreDate] = useState(today);
  const [answers, setAnswers] = useState(Object.fromEntries(QUESTIONS.map(q => [q.key, null])));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/poem");
      setScores(data || []);
    } catch (err) {
      console.error("Failed to load POEM scores:", err);
      toast.error("Could not load POEM history");
    }
  };
  useEffect(() => { load(); }, []);

  const total = QUESTIONS.reduce((sum, q) => sum + (answers[q.key] ?? 0), 0);
  const allAnswered = QUESTIONS.every(q => answers[q.key] !== null);

  const save = async () => {
    if (!allAnswered) { toast.error("Please answer all 7 questions"); return; }
    setBusy(true);
    try {
      await api.post("/poem", { score_date: scoreDate, answers, notes });
      toast.success(`POEM saved · ${total}/28 — ${bandLabel(total)}`);
      setAnswers(Object.fromEntries(QUESTIONS.map(q => [q.key, null])));
      setNotes("");
      setScoreDate(today);
      await load();
      setTab("history");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this POEM score?")) return;
    try { await api.delete(`/poem/${id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Failed to delete"); }
  };

  const trend = [...scores].reverse().map(s => ({ date: s.score_date, total: s.total }));

  return (
    <AppShell>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-sage-500">Patient-Oriented Eczema Measure</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900 mt-2">POEM score</h1>
        </div>
        <Button onClick={() => setTab("new")} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white" data-testid="poem-new-btn">
          <ClipboardList className="w-4 h-4 mr-2"/>New score
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-white border border-stone-100 rounded-full p-1" data-testid="poem-tabs">
          <TabsTrigger value="history" className="rounded-full data-[state=active]:bg-sage-400 data-[state=active]:text-white" data-testid="poem-tab-history">History</TabsTrigger>
          <TabsTrigger value="new" className="rounded-full data-[state=active]:bg-sage-400 data-[state=active]:text-white" data-testid="poem-tab-new">New score</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-6 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="poem-trend-card">
            <h3 className="font-serif text-2xl text-stone-900">POEM trend</h3>
            <div className="h-64 mt-4">
              {scores.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-stone-400 text-sm">
                  <p>No POEM scores yet — take your first one.</p>
                  <button onClick={() => setTab("new")} className="mt-3 text-sage-500 underline text-sm">Start now</button>
                </div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={trend}>
                    <CartesianGrid stroke="#E7EDE3" strokeDasharray="3 3" vertical={false}/>
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6F7A6A" }} stroke="#D4DCCD"/>
                    <YAxis domain={[0, 28]} tick={{ fontSize: 11, fill: "#6F7A6A" }} stroke="#D4DCCD"/>
                    <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #E2E9DD", borderRadius: 12 }} />
                    <Line type="monotone" dataKey="total" stroke="#2F9A3B" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="space-y-3" data-testid="poem-list">
            {scores.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl p-5 border border-stone-100 flex items-center gap-4" data-testid={`poem-row-${s.id}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center font-mono text-xl font-semibold ${bandColor(s.total)}`}>
                  {s.total}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-xl text-stone-900">{s.score_date}</p>
                  <p className="text-sm text-stone-500">{s.band} · /28</p>
                  {s.notes && <p className="text-xs text-stone-400 mt-1 truncate">{s.notes}</p>}
                </div>
                <button onClick={() => remove(s.id)} className="text-stone-400 hover:text-rose-500 p-2 rounded-full transition-colors" data-testid={`poem-delete-${s.id}`}>
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="new" className="mt-6">
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-100" data-testid="poem-form">
            <p className="text-sm text-stone-500 leading-relaxed">For each question, choose how often it applied <strong>over the last week</strong>.</p>

            <div className="mt-4 max-w-xs">
              <Label htmlFor="poem-date">Date of assessment</Label>
              <Input id="poem-date" type="date" value={scoreDate} onChange={(e) => setScoreDate(e.target.value)} className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl" data-testid="poem-date-input"/>
            </div>

            <div className="mt-8 space-y-7">
              {QUESTIONS.map((q, i) => (
                <div key={q.key} data-testid={`poem-q-${q.key}`}>
                  <div className="flex items-baseline gap-3">
                    <span className="text-sage-500 font-mono text-sm">{i + 1}.</span>
                    <p className="text-base text-stone-800 font-medium">{q.label}</p>
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 ml-7">
                    {OPTIONS.map((o) => {
                      const active = answers[q.key] === o.value;
                      return (
                        <button
                          key={o.value}
                          onClick={() => setAnswers((a) => ({ ...a, [q.key]: o.value }))}
                          className={`px-3 py-2 rounded-xl text-xs sm:text-sm transition-all border ${active ? "bg-sage-500 text-white border-sage-500" : "bg-stone-50 border-stone-200 text-stone-700 hover:bg-stone-100"}`}
                          data-testid={`poem-q-${q.key}-opt-${o.value}`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 max-w-2xl">
              <Label htmlFor="poem-notes">Notes (optional)</Label>
              <Textarea id="poem-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else to capture about this week?" className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl min-h-[90px]" data-testid="poem-notes-input"/>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4 pt-6 border-t border-stone-100">
              <div className="flex items-center gap-3">
                <span className={`w-14 h-14 rounded-full flex items-center justify-center font-mono text-lg font-semibold ${bandColor(total)}`} data-testid="poem-current-total">{total}</span>
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500">Current total</p>
                  <p className="font-serif text-xl text-stone-900" data-testid="poem-current-band">{allAnswered ? bandLabel(total) : "—"}</p>
                </div>
              </div>
              <Button onClick={save} disabled={busy || !allAnswered} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white px-7 py-6" data-testid="poem-save-btn">
                {busy ? "Saving..." : "Save POEM score"}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
